import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useControlAuth } from '../../ControlAuthContext';
import './EditQuote.css';

const EditQuote = () => {
  const { id } = useParams();
  const numero_cotizacion = id;
  const navigate = useNavigate();
  const { user } = useControlAuth();

  const today = new Date();
  const formattedToday = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const [form, setForm] = useState({
    client_email: '',
    client_name: '',
    client_category_territory: '',
    comments: '',
    status: 'Pending Quote',
    lost_reason: '',
    items: []
  });

  // Campos para crear la orden cuando se elige "PO"
  const [orderFields, setOrderFields] = useState({
    date: formattedToday,
    week: '',
    trimester: '',
    orderid: '',
    idclient: '',
    newClient: false,
    newClientName: '',
    newClientEmail: '',
    newClientPassword: '',
    shippingAddress: '',
    billingAddress: '',
    zipCode: '',
    description: '',
    comments: '',
    delivery_date: '',
    userid: '',
    margins: '',
    commission: '',
  });

  const [clients, setClients] = useState([]);
  const [controlUsers, setControlUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [originalNumero, setOriginalNumero] = useState('');

  // Cargar la cotización
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const token = localStorage.getItem('controlToken');
        const res = await axios.get(`https://valleyapi-dr9y.onrender.com/api/control/quotes/group/${encodeURIComponent(numero_cotizacion)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const quoteItems = res.data.quotes || [];

        if (quoteItems.length === 0) {
          toast.error('Quote not found');
          navigate('/control/pending-quotes');
          return;
        }

        const first = quoteItems[0];

        setForm({
          client_email: first.client_email || '',
          client_name: first.client_name || '',
          client_category_territory: first.client_category_territory || '',
          comments: first.Comments || '',
          status: first.status || 'Pending Quote',
          lost_reason: first.lost_reason || '',
          items: quoteItems.map(item => ({
            idcotizacion: item.idcotizacion,
            product_sku: item.product_sku || '',
            brand: item.brand || '',
            product_price: item.product_price || '',
            product_quantity: item.product_quantity || '',
            delivery_time: item.delivery_time || ''
          }))
        });

        setOriginalNumero(numero_cotizacion);
      } catch (err) {
        console.error(err);
        toast.error('Error loading quote');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [numero_cotizacion, navigate]);

  // Cargar clientes y usuarios de control
  useEffect(() => {
    const fetchSupportingData = async () => {
      const token = localStorage.getItem('controlToken');
      try {
        const clientsRes = await axios.get('https://valleyapi-dr9y.onrender.com/api/control/orders/clients', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClients(clientsRes.data.clients || []);

        if (user?.accesstype === 0) {
          const usersRes = await axios.get('https://valleyapi-dr9y.onrender.com/api/control/orders/users', {
            headers: { Authorization: `Bearer ${token}` }
          });
          setControlUsers(usersRes.data.users || []);
        }
      } catch (err) {
        console.error('Error fetching clients/users:', err);
      }
    };

    if (user) fetchSupportingData();
  }, [user]);

  // Calcular semana y trimestre
  useEffect(() => {
    if (orderFields.date) {
      const d = new Date(orderFields.date);
      const weekNumber = Math.ceil(((d - new Date(d.getFullYear(), 0, 1)) / 86400000 + new Date(d.getFullYear(), 0, 1).getDay() + 1) / 7);
      const trimesterVal = Math.floor(d.getMonth() / 3) + 1;
      setOrderFields(prev => ({ ...prev, week: weekNumber, trimester: trimesterVal }));
    }
  }, [orderFields.date]);

  // Auto-fill client addresses
  useEffect(() => {
    if (!orderFields.newClient && orderFields.idclient) {
      const selectedClient = clients.find(client => client.id === parseInt(orderFields.idclient));
      if (selectedClient) {
        setOrderFields(prev => ({
          ...prev,
          shippingAddress: selectedClient.shipping_address || '',
          billingAddress: selectedClient.billing_address || '',
          zipCode: selectedClient.zipCode || ''
        }));
      }
    }
  }, [orderFields.idclient, orderFields.newClient, clients]);

  const updateItem = (index, field, value) => {
    setForm(prev => {
      const newItems = [...prev.items];
      newItems[index][field] = value;
      return { ...prev, items: newItems };
    });
  };

  const addItem = () => {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { 
        product_sku: '', 
        product_price: '', 
        product_quantity: '', 
        delivery_time: '',
        brand: '' 
      }]
    }));
  };

  const removeItem = (index) => {
    if (form.items.length === 1) {
      toast.error('You must have at least one product');
      return;
    }
    setForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleOrderChange = (field, value) => {
    setOrderFields(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.client_email) return toast.error('Client email is required');
    if (form.items.some(item => !item.product_sku || !item.product_quantity)) {
      return toast.error('All products must have SKU and quantity');
    }
    if (form.status === 'Lost Client' && !form.lost_reason.trim()) {
      return toast.error('Please enter lost reason');
    }
    if (form.status === 'PO' && !orderFields.orderid.trim()) {
      return toast.error('Order ID is required when converting to PO');
    }

    const token = localStorage.getItem('controlToken');

    try {
      if (form.status === 'PO') {
        // ========== CONVERSIÓN A PO ==========
        // 1. Eliminar la cotización primero (usando los idcotizacion actuales)
        const quoteIds = form.items.map(item => item.idcotizacion);
        await axios.delete('https://valleyapi-dr9y.onrender.com/api/control/quotes', {
          headers: { Authorization: `Bearer ${token}` },
          data: { ids: quoteIds }
        });

        // 2. Crear la orden en control_order + control_order_items
        const orderBody = {
          date: orderFields.date,
          week: orderFields.week,
          trimester: orderFields.trimester,
          orderid: orderFields.orderid,
          idclient: orderFields.newClient ? null : orderFields.idclient,
          newClient: orderFields.newClient,
          newClientName: orderFields.newClient ? orderFields.newClientName : null,
          newClientEmail: orderFields.newClient ? orderFields.newClientEmail : null,
          newClientPassword: orderFields.newClient ? orderFields.newClientPassword : null,
          shippingAddress: orderFields.shippingAddress,
          billingAddress: orderFields.billingAddress,
          zipCode: orderFields.zipCode,
          description: orderFields.description || form.comments,
          status: 'Pending Payment',
          comments: orderFields.comments || form.comments,
          delivery_date: orderFields.delivery_date || null,
          userid: orderFields.userid || (user?.accesstype === 1 ? user.userid : ''),
          margins: orderFields.margins || null,
          commission: orderFields.commission || null,
          items: form.items.map(item => ({
            product: item.product_sku || '',
            brand: item.brand || '',
            description: item.delivery_time || '',
            cost: 0,
            price: parseFloat(item.product_price) || 0,
            quantity: parseInt(item.product_quantity) || 0,
            skuInterno: item.product_sku || ''
          }))
        };

        await axios.post('https://valleyapi-dr9y.onrender.com/api/control/orders', orderBody, {
          headers: { Authorization: `Bearer ${token}` }
        });

        toast.success('Quote converted to PO and moved to Orders successfully!');
      } else {
        // ========== ACTUALIZACIÓN NORMAL (no PO) ==========
        await axios.put(
          `https://valleyapi-dr9y.onrender.com/api/control/quotes/group/${originalNumero}`,
          {
            client_email: form.client_email,
            client_name: form.client_name,
            client_category_territory: form.client_category_territory,
            comments: form.comments,
            status: form.status,
            lost_reason: form.lost_reason,
            items: form.items
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success('Quote updated successfully');
      }

      navigate('/control/pending-quotes');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Error saving changes');
    }
  };

  if (loading) return <div>Loading quote...</div>;

  return (
    <div className="edit-quote-container">
      <div className="edit-quote-header">
        <h2>Edit Quote #{originalNumero}</h2>
      </div>
      
      <form onSubmit={handleSubmit} className="edit-quote-form">
        <div className="edit-quote-field">
          <label>Client Email</label>
          <input type="email" value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} required />
        </div>

        <div className="edit-quote-field">
          <label>Client Name</label>
          <input type="text" value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} />
        </div>

        <div className="edit-quote-field">
          <label>Client/Category/Territory</label>
          <input type="text" value={form.client_category_territory} onChange={e => setForm({ ...form, client_category_territory: e.target.value })} />
        </div>

        <div className="edit-quote-field">
          <label>Status</label>
          <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} required>
            <option value="Quoted Submitted">Quoted Submitted</option>
            <option value="Pending Quote">Pending Quote</option>
            <option value="PO">PO</option>
            <option value="Lost Client">Lost Client</option>
            <option value="Cancelled">Cancelled</option>
            <option value="Sent">Sent</option>
          </select>
        </div>

        {form.status === 'Lost Client' && (
          <div className="edit-quote-field">
            <label style={{ color: '#c62828' }}>Motivo de pérdida del cliente (requerido)</label>
            <textarea value={form.lost_reason} onChange={e => setForm({ ...form, lost_reason: e.target.value })} required />
          </div>
        )}

        <div className="edit-quote-field">
          <label>Comments / Notas adicionales</label>
          <textarea value={form.comments} onChange={e => setForm({ ...form, comments: e.target.value })} />
        </div>

        {/* ====================== SECCIÓN PO ====================== */}
        {form.status === 'PO' && (
          <div style={{ marginTop: '40px', padding: '30px', border: '4px solid #2e7d32', borderRadius: '12px', backgroundColor: '#f8fff8' }}>
            <h3 style={{ color: '#2e7d32', marginBottom: '20px' }}>📋 Convert to Purchase Order</h3>

            <div className="edit-quote-field">
              <label>Creation Date</label>
              <input type="date" value={orderFields.date} onChange={e => handleOrderChange('date', e.target.value)} required />
            </div>

            <div className="edit-quote-field">
              <label>Order ID (manual)</label>
              <input type="text" value={orderFields.orderid} onChange={e => handleOrderChange('orderid', e.target.value)} placeholder="e.g. PO-2026-001" required />
            </div>

            <div className="edit-quote-field">
              <label>
                <input type="checkbox" checked={orderFields.newClient} onChange={e => handleOrderChange('newClient', e.target.checked)} />
                {' '}Create New Client
              </label>
            </div>

            {orderFields.newClient ? (
              <>
                <div className="edit-quote-field">
                  <label>New Client Name</label>
                  <input type="text" value={orderFields.newClientName} onChange={e => handleOrderChange('newClientName', e.target.value)} required />
                </div>
                <div className="edit-quote-field">
                  <label>New Client Email</label>
                  <input type="email" value={orderFields.newClientEmail} onChange={e => handleOrderChange('newClientEmail', e.target.value)} required />
                </div>
                <div className="edit-quote-field">
                  <label>New Client Password</label>
                  <input type="password" value={orderFields.newClientPassword} onChange={e => handleOrderChange('newClientPassword', e.target.value)} required />
                </div>
              </>
            ) : (
              <div className="edit-quote-field">
                <label>Existing Client</label>
                <select value={orderFields.idclient} onChange={e => handleOrderChange('idclient', e.target.value)} required>
                  <option value="">Select Client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name} ({client.email})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="edit-quote-field">
              <label>Shipping Address</label>
              <input type="text" value={orderFields.shippingAddress} onChange={e => handleOrderChange('shippingAddress', e.target.value)} required />
            </div>
            <div className="edit-quote-field">
              <label>Billing Address</label>
              <input type="text" value={orderFields.billingAddress} onChange={e => handleOrderChange('billingAddress', e.target.value)} required />
            </div>
            <div className="edit-quote-field">
              <label>Zip Code</label>
              <input type="text" value={orderFields.zipCode} onChange={e => handleOrderChange('zipCode', e.target.value)} required />
            </div>

            <div className="edit-quote-field">
              <label>Description</label>
              <textarea value={orderFields.description} onChange={e => handleOrderChange('description', e.target.value)} />
            </div>

            <div className="edit-quote-field">
              <label>Delivery Date</label>
              <input type="date" value={orderFields.delivery_date} onChange={e => handleOrderChange('delivery_date', e.target.value)} />
            </div>

            {user?.accesstype === 0 && (
              <div className="edit-quote-field">
                <label>Owner</label>
                <select value={orderFields.userid} onChange={e => handleOrderChange('userid', e.target.value)} required>
                  <option value="">Select Owner</option>
                  {controlUsers.map(u => (
                    <option key={u.userid} value={u.userid}>{u.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="edit-quote-field">
              <label>Margins</label>
              <input type="number" step="0.01" value={orderFields.margins} onChange={e => handleOrderChange('margins', e.target.value)} />
            </div>
            <div className="edit-quote-field">
              <label>Commission</label>
              <input type="number" step="0.01" value={orderFields.commission} onChange={e => handleOrderChange('commission', e.target.value)} />
            </div>
          </div>
        )}

        <h3 style={{ color: '#2e7d32', marginTop: '30px', marginBottom: '15px' }}>Products in this Quote</h3>
        
        {form.items.map((item, index) => (
          <div key={index} className="edit-product-card">
            <div className="edit-product-row">
              <input placeholder="Brand" value={item.brand} onChange={e => updateItem(index, 'brand', e.target.value)} />
              <input placeholder="Product SKU" value={item.product_sku} onChange={e => updateItem(index, 'product_sku', e.target.value)} required />
            </div>
            <div className="edit-product-row">
              <input type="number" step="0.01" placeholder="Price" value={item.product_price} onChange={e => updateItem(index, 'product_price', e.target.value)} />
              <input type="number" placeholder="Quantity" value={item.product_quantity} onChange={e => updateItem(index, 'product_quantity', e.target.value)} required />
              <input placeholder="Delivery Time" value={item.delivery_time} onChange={e => updateItem(index, 'delivery_time', e.target.value)} />
              {form.items.length > 1 && (
                <button type="button" onClick={() => removeItem(index)} style={{ background: '#ffebee', color: '#c62828', border: 'none', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer' }}>
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}

        <button type="button" onClick={addItem} className="edit-btn-add">+ Add Another Product</button>

        <div className="edit-form-actions">
          <button type="submit" className="edit-btn-submit">Save Entire Quote</button>
          <button type="button" className="edit-btn-cancel" onClick={() => navigate('/control/pending-quotes')}>Cancel</button>
        </div>
      </form>
    </div>
  );
};

export default EditQuote;
